import { defineFeature } from '../define-feature';
import { TEST_IDS, sidebarNavTestId, sidebarSubTestId } from '../test-ids';

export default defineFeature({
  id: 'website.domain.connect',
  category: 'website',
  title: 'Connecter un nom de domaine',
  intent:
    "Relier un nom de domaine à vous à votre site — en acheter un ou rattacher celui que vous possédez déjà — pour remplacer l'adresse temporaire en .propelsia.site par votre propre adresse.",
  context: {
    why: "L'adresse en .propelsia.site est un point de départ : votre site est en ligne tout de suite, sans rien acheter. Mais un nom de domaine à vous change la donne. Il inspire confiance — un client retient et fait davantage confiance à « mon-salon.fr » qu'à une adresse partagée. Il renforce le référencement, parce que votre marque devient votre adresse. Et il débloque une adresse e-mail professionnelle à votre nom de domaine. Propelsia gère le domaine pour vous : vous cherchez un nom disponible et l'achetez en quelques clics, ou vous rattachez un domaine que vous possédez déjà en le transférant. Il n'y a pas d'enregistrements techniques à recopier ni de manipulation à faire chez un prestataire tiers — une fois le domaine choisi et payé, l'activation se fait automatiquement, et votre site bascule sur votre adresse.",
    use_cases: [
      "Acheter « mon-salon.fr » et en faire l'adresse principale de votre site.",
      'Rattacher un domaine que vous possédez déjà en le transférant vers Propelsia.',
      "Basculer l'adresse affichée à vos visiteurs de l'adresse temporaire vers votre propre domaine.",
      'Ajouter un second domaine qui redirige vers votre site principal.',
    ],
  },
  required_scopes: ['website.builder.update'],
  ui_entry_points: [{ route: '/website/domain', element: 'Bouton « Ajouter un domaine »' }],
  api: {
    primary_operation_id: 'ProWebsiteAppController_selectDomain',
    related_operation_ids: [
      'ProWebsiteAppController_searchDomain',
      'ProWebsiteAppController_transferDomain',
      'ProWebsiteAppController_addSecondaryDomain',
      'ProWebsiteAppController_changePrimaryDomain',
      'ProWebsiteAppController_removeDomain',
      'ProWebsiteAppController_getDnsSuggestions',
      'ProWebsiteAppController_getDomainPaymentLink',
      'ProWebsiteAppController_listDomains',
      'ProWebsiteAppController_getStatus',
    ],
  },
  steps: [
    '**Ouvrez la gestion du domaine** depuis Site internet, écran « Nom de domaine ». Vous y voyez votre adresse actuelle et le bouton pour en ajouter une à vous.',
    '**Recherchez un domaine disponible** en tapant le nom souhaité, ou **indiquez un domaine que vous possédez déjà** pour le transférer.',
    "**Choisissez le domaine** et réglez l'achat : Propelsia s'occupe de la configuration, vous n'avez pas d'enregistrements techniques à recopier ailleurs.",
    "**Définissez le domaine principal** : c'est l'adresse affichée à vos visiteurs. Un éventuel second domaine redirige vers celui-ci.",
    "**Laissez l'activation se faire** : une fois le paiement validé, votre site bascule automatiquement sur votre adresse (comptez un court délai de propagation).",
  ],
  gotchas: [
    "**Acheter ou transférer : deux chemins pour le même résultat.** Soit vous cherchez un nom libre et vous l'achetez via Propelsia, soit vous rattachez un domaine déjà à vous en le transférant. Dans les deux cas, il n'y a pas de table d'enregistrements DNS à remplir vous-même : Propelsia gère la configuration.",
    "**L'activation est automatique, pas instantanée.** Après le paiement, le rattachement se fait tout seul, mais la propagation sur Internet demande un court délai. Pendant ce laps de temps, votre adresse temporaire reste accessible.",
    "**L'adresse en .propelsia.site est temporaire.** Connecter votre propre domaine la remplace comme adresse principale. L'adresse temporaire n'est pas une limite à corriger dans l'urgence : votre site fonctionne parfaitement avec, le domaine propre est un plus.",
    "**Domaine principal et domaine secondaire.** Le principal est l'adresse montrée à vos visiteurs ; un secondaire redirige vers le principal (utile si vous avez plusieurs orthographes ou extensions). Vous pouvez changer lequel est principal à tout moment.",
    "**Votre domaine sert au-delà du site.** C'est la même adresse qui alimente vos redirections et votre adresse e-mail professionnelle. Bien choisi une fois, il sert partout.",
  ],
  related_features: [
    'website.builder.publish',
    'website.identity.manage',
    'website.redirects.manage',
    'website.email.configure',
  ],
  demo_script: {
    // Flux d'achat + paiement externe : démo sobre (on montre le point d'entrée),
    // le paiement et l'activation automatique ne sont pas rejoués. Cf. règle §3.
    duration_target_s: 25,
    steps: [
      {
        action: 'navigate',
        value: '/fr-FR',
        caption:
          'Un nom de domaine à vous inspire confiance et remplace l’adresse temporaire — voyons comment le connecter.',
      },
      { action: 'click', target: sidebarNavTestId('/website'), caption: 'Menu latéral, Site internet.' },
      { action: 'click', target: sidebarSubTestId('/website/domain'), caption: 'Onglet Nom de domaine.' },
      {
        action: 'expect_visible',
        target: TEST_IDS.domain_add,
        caption: 'Le bouton « Ajouter un domaine » vous laisse en acheter un ou transférer le vôtre.',
        capture: true,
      },
    ],
  },
});

// 💬 Verbatim Kaltouma
// > (Ancré code.) Connecter un nom de domaine perso (achat ou transfert) pour remplacer le
// > sous-domaine temporaire .propelsia.site ; gestion DNS guidée, domaine principal/secondaire.
